import type { MailSyncState } from '@worki/shared';

export function scanRemainingSeconds(state: MailSyncState, elapsed: number): number | null {
  if (state.total === null || state.scanned < 5 || elapsed < 3 || state.queuedWork ||
      state.phase === 'queued' || state.phase === 'discovering' || state.phase === 'refining' || state.phase === 'recovering') return null;
  const rate=state.recentSecondsPerMessage ?? elapsed/state.scanned;
  return Math.max(1,Math.round(rate*(state.total-state.scanned)));
}

/**
 * What the invoices page last saw of one mailbox's scan: enough to tell, on the
 * next look, whether the imported count moved and whether a scan ended in
 * between. A mailbox with no scan is remembered too — "no scan" is a look, and
 * the scan that follows it is new.
 */
export interface SeenScan {
  status: MailSyncState['status'] | null;
  imported: number;
  startedAt: string | null;
}

export function seenScan(scan: MailSyncState | null | undefined): SeenScan {
  return { status: scan?.status ?? null, imported: scan?.imported ?? 0, startedAt: scan?.startedAt ?? null };
}

/**
 * Whether `scan` finished since the page last looked at this mailbox — the
 * moment its outcome is announced, once.
 *
 * A scan seen running and now stopped has. So has a stopped scan the page never
 * saw running at all, told apart from the one it last saw by when it started.
 * That is the scan that fails at its first step: a provider refusing the login
 * answers at once, and the scan is over before the refetch that follows the
 * button comes back. It is also the scan whose outcome matters most (INV-010).
 *
 * The first look at a mailbox announces nothing. A scan that ended before the
 * page opened was announced where it ran, if anywhere.
 */
export function scanFinished(previous: SeenScan | undefined, scan: MailSyncState | null | undefined): boolean {
  if (!previous || !scan || scan.status === 'running') return false;
  return previous.status === 'running' || previous.startedAt !== scan.startedAt;
}
